import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
    Calendar,
    CalendarPlus,
    Loader2,
    RefreshCw,
    Trash2,
    CheckCircle2,
    AlertTriangle,
    Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SlotGenerator = ({ token, sessions, onSlotsGenerated }) => {
    const [loading, setLoading] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [config, setConfig] = useState({
        session_ids: [],
        days: 30,
        times: ['09:00', '14:00'],
        capacity: 4,
        skip_weekends: false,
        specific_days: [] // NEW: for recurring patterns like "every Thursday"
    });
    const [result, setResult] = useState(null);
    const [useRecurringPattern, setUseRecurringPattern] = useState(false);
    
    // Bulk delete state
    const [showBulkDelete, setShowBulkDelete] = useState(false);
    const [bulkDeleteConfig, setBulkDeleteConfig] = useState({
        start_date: '',
        end_date: '',
        session_id: '',
        time: ''
    });
    const [deletingBulk, setDeletingBulk] = useState(false);

    const authAxios = axios.create({
        baseURL: API,
        headers: { 'Authorization': `Bearer ${token}` }
    });

    const handleSessionToggle = (sessionId) => {
        setConfig(prev => ({
            ...prev,
            session_ids: prev.session_ids.includes(sessionId)
                ? prev.session_ids.filter(id => id !== sessionId)
                : [...prev.session_ids, sessionId]
        }));
    };

    const handleTimeToggle = (time) => {
        setConfig(prev => ({
            ...prev,
            times: prev.times.includes(time)
                ? prev.times.filter(t => t !== time)
                : [...prev.times, time]
        }));
    };

    const handleDayToggle = (day) => {
        setConfig(prev => ({
            ...prev,
            specific_days: prev.specific_days.includes(day)
                ? prev.specific_days.filter(d => d !== day)
                : [...prev.specific_days, day]
        }));
    };

    const selectAllSessions = () => {
        setConfig(prev => ({
            ...prev,
            session_ids: sessions.map(s => s.id)
        }));
    };

    const generateSlots = async () => {
        if (config.session_ids.length === 0) {
            toast.error('Please select at least one session type');
            return;
        }
        if (config.times.length === 0) {
            toast.error('Please select at least one time slot');
            return;
        }
        if (useRecurringPattern && config.specific_days.length === 0) {
            toast.error('Please select at least one day of the week');
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const payload = {
                ...config,
                use_specific_days: useRecurringPattern
            };
            const response = await authAxios.post('/admin/generate-month-slots', payload);
            setResult(response.data);
            toast.success(`Generated ${response.data.created_count} slots!`);
            if (onSlotsGenerated) onSlotsGenerated();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate slots');
        } finally {
            setLoading(false);
        }
    };

    const clearFutureSlots = async () => {
        if (!window.confirm('Are you sure you want to delete ALL future slots? This cannot be undone.')) {
            return;
        }

        setClearing(true);
        try {
            const response = await authAxios.delete('/admin/clear-future-slots');
            toast.success(`Deleted ${response.data.deleted_count} slots`);
            if (onSlotsGenerated) onSlotsGenerated();
        } catch (error) {
            toast.error('Failed to clear slots');
        } finally {
            setClearing(false);
        }
    };

    const [deletingAll, setDeletingAll] = useState(false);
    
    const deleteAllSlots = async () => {
        if (!window.confirm('⚠️ WARNING: This will delete ALL slots (past and future). Are you absolutely sure?')) {
            return;
        }
        const confirmation = window.prompt('Type DELETE to confirm (case-insensitive):');
        if (!confirmation || confirmation.toUpperCase() !== 'DELETE') {
            toast.error('Deletion cancelled - you must type DELETE to confirm');
            return;
        }

        setDeletingAll(true);
        try {
            const response = await authAxios.delete('/admin/delete-all-slots');
            toast.success(`Deleted ${response.data.deleted_count} slots`);
            if (onSlotsGenerated) onSlotsGenerated();
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Delete all slots error:', error);
            }
            toast.error(error.response?.data?.detail || 'Failed to delete all slots');
        } finally {
            setDeletingAll(false);
        }
    };

    const bulkDeleteSlots = async () => {
        if (!bulkDeleteConfig.start_date || !bulkDeleteConfig.end_date) {
            toast.error('Please select start and end dates');
            return;
        }
        
        if (!window.confirm(`Delete slots from ${bulkDeleteConfig.start_date} to ${bulkDeleteConfig.end_date}?`)) {
            return;
        }

        setDeletingBulk(true);
        try {
            const response = await authAxios.post('/admin/bulk-delete-slots', bulkDeleteConfig);
            toast.success(`Deleted ${response.data.deleted_count} slots`);
            setBulkDeleteConfig({ start_date: '', end_date: '', session_id: '', time: '' });
            setShowBulkDelete(false);
            if (onSlotsGenerated) onSlotsGenerated();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete slots');
        } finally {
            setDeletingBulk(false);
        }
    };

    const timeOptions = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
    const dayOptions = [
        { value: 0, label: 'Mon' },
        { value: 1, label: 'Tue' },
        { value: 2, label: 'Wed' },
        { value: 3, label: 'Thu' },
        { value: 4, label: 'Fri' },
        { value: 5, label: 'Sat' },
        { value: 6, label: 'Sun' }
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <CalendarPlus className="w-5 h-5" />
                    Generate Slots
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Session Selection */}
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <Label className="text-sm font-medium">Select Sessions</Label>
                        <Button variant="ghost" size="sm" onClick={selectAllSessions}>
                            Select All
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {sessions.map(session => (
                            <button
                                key={session.id}
                                onClick={() => handleSessionToggle(session.id)}
                                className={`p-3 border rounded-lg text-left transition-colors ${
                                    config.session_ids.includes(session.id)
                                        ? 'border-primary bg-primary/10'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <p className="font-medium text-sm">{session.name}</p>
                                <p className="text-xs text-muted-foreground">£{session.price}</p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Recurring Pattern Toggle */}
                <div className="bg-secondary/30 rounded-lg p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={useRecurringPattern}
                            onChange={(e) => setUseRecurringPattern(e.target.checked)}
                            className="w-5 h-5 rounded border-gray-300"
                        />
                        <div>
                            <span className="font-medium">Use Recurring Pattern</span>
                            <p className="text-xs text-muted-foreground">e.g., every Thursday at 10:00 and every Friday at 14:00</p>
                        </div>
                    </label>
                </div>

                {/* Day Selection (for recurring pattern) */}
                {useRecurringPattern && (
                    <div>
                        <Label className="text-sm font-medium mb-2 block">Select Days of Week</Label>
                        <div className="flex flex-wrap gap-2">
                            {dayOptions.map(day => (
                                <button
                                    key={day.value}
                                    onClick={() => handleDayToggle(day.value)}
                                    className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                                        config.specific_days.includes(day.value)
                                            ? 'border-primary bg-primary text-white'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    {day.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Time Selection */}
                <div>
                    <Label className="text-sm font-medium mb-2 block">Select Times</Label>
                    <div className="flex flex-wrap gap-2">
                        {timeOptions.map(time => (
                            <button
                                key={time}
                                onClick={() => handleTimeToggle(time)}
                                className={`px-3 py-1 border rounded-full text-sm transition-colors ${
                                    config.times.includes(time)
                                        ? 'border-primary bg-primary text-white'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Days & Capacity */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="days" className="text-sm font-medium">Days to Generate</Label>
                        <Input
                            id="days"
                            type="number"
                            min="1"
                            max="90"
                            value={config.days}
                            onChange={(e) => setConfig({...config, days: parseInt(e.target.value) || 30})}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label htmlFor="capacity" className="text-sm font-medium">Capacity per Slot</Label>
                        <Input
                            id="capacity"
                            type="number"
                            min="1"
                            max="20"
                            value={config.capacity}
                            onChange={(e) => setConfig({...config, capacity: parseInt(e.target.value) || 4})}
                            className="mt-1"
                        />
                    </div>
                </div>

                {/* Skip Weekends (only show if not using recurring pattern) */}
                {!useRecurringPattern && (
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.skip_weekends}
                            onChange={(e) => setConfig({...config, skip_weekends: e.target.checked})}
                            className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm">Skip weekends (Sat/Sun)</span>
                    </label>
                )}

                {/* Result Display */}
                {result && (
                    <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                            {result.success ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                            ) : (
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            )}
                            <span className="font-medium">{result.message}</span>
                        </div>
                        {result.date_range && (
                            <p className="text-sm text-muted-foreground">
                                Date range: {result.date_range.start} to {result.date_range.end}
                            </p>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <Button 
                        onClick={generateSlots} 
                        disabled={loading || config.session_ids.length === 0}
                        className="flex-1"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <CalendarPlus className="w-4 h-4 mr-2" />
                                Generate {config.days} Days of Slots
                            </>
                        )}
                    </Button>
                    <Button 
                        variant="destructive" 
                        onClick={clearFutureSlots}
                        disabled={clearing}
                        title="Delete ALL future slots"
                    >
                        {clearing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Trash2 className="w-4 h-4" />
                        )}
                    </Button>
                </div>

                {/* Info */}
                <p className="text-xs text-muted-foreground">
                    {useRecurringPattern 
                        ? `This will create slots on ${config.specific_days.length} day(s) per week × ${config.times.length} time(s) for ${config.days} days.`
                        : `This will create slots for ${config.session_ids.length} session(s) × ${config.times.length} time(s) × ${config.days} days = up to ${config.session_ids.length * config.times.length * config.days} slots.`
                    } Existing slots will be skipped.
                </p>

                {/* Bulk Delete Section */}
                <div className="border-t pt-4">
                    <Button 
                        variant="outline" 
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setShowBulkDelete(!showBulkDelete)}
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Bulk Delete Slots
                    </Button>
                    
                    {showBulkDelete && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
                            <p className="text-sm text-red-800 font-medium">Delete multiple slots by date range:</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm">Start Date</Label>
                                    <Input 
                                        type="date" 
                                        value={bulkDeleteConfig.start_date}
                                        onChange={(e) => setBulkDeleteConfig({...bulkDeleteConfig, start_date: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm">End Date</Label>
                                    <Input 
                                        type="date" 
                                        value={bulkDeleteConfig.end_date}
                                        onChange={(e) => setBulkDeleteConfig({...bulkDeleteConfig, end_date: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm">Session (optional)</Label>
                                    <select 
                                        value={bulkDeleteConfig.session_id}
                                        onChange={(e) => setBulkDeleteConfig({...bulkDeleteConfig, session_id: e.target.value})}
                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                    >
                                        <option value="">All sessions</option>
                                        {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-sm">Time (optional)</Label>
                                    <select 
                                        value={bulkDeleteConfig.time}
                                        onChange={(e) => setBulkDeleteConfig({...bulkDeleteConfig, time: e.target.value})}
                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                    >
                                        <option value="">All times</option>
                                        {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                            </div>
                            <Button 
                                variant="destructive" 
                                className="w-full"
                                onClick={bulkDeleteSlots}
                                disabled={deletingBulk}
                            >
                                {deletingBulk ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                                ) : (
                                    <><Trash2 className="w-4 h-4 mr-2" />Delete Matching Slots</>
                                )}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Delete ALL Slots - Danger Zone */}
                <div className="border-t pt-4 mt-4">
                    <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
                        <h4 className="text-red-800 font-bold mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            Danger Zone
                        </h4>
                        <p className="text-red-700 text-sm mb-3">
                            Delete ALL slots from the system. This action cannot be undone.
                        </p>
                        <Button 
                            variant="destructive" 
                            className="w-full bg-red-600 hover:bg-red-700"
                            onClick={deleteAllSlots}
                            disabled={deletingAll}
                        >
                            {deletingAll ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting All Slots...</>
                            ) : (
                                <><Trash2 className="w-4 h-4 mr-2" />Delete ALL Slots</>
                            )}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default SlotGenerator;
